<div>
  <ng-container *ngTemplateOutlet="viewerTemplate"></ng-container>

  <!-- Viewer -->
  <ng-template #viewerTemplate>
    <ng-container *ngIf="{
        layout: layout$ | async,
        pipelines: micro.pipelines$ | async,
        categories: micro.visibleCategories$ | async,
        labels: micro.visibleOptions$ | async,
        steps: micro.steps$ | async,
        segmentation: micro.segmentation$ | async,
        userAnalysts: micro.userAnalysts$ | async,
        aiAlgorithms: micro.aiAlgorithms$ | async,
        meOption: micro.currentUserAsAnalystOption$ | async,
        breadcrumbAnalysts: micro.analystsExcludingCurrentUser$ | async,
        selectedAnalyst: micro.selectedAnalyst$ | async,
        canEdit: micro.canEdit$ | async,
        editingActive: micro.editingActive$ | async,
        isReviewingOtherAnalyst: micro.isReviewingOtherAnalyst$ | async,
        editingEnabled: micro.editingEnabled$ | async,
        acquisitionFields: micro.acquisitionFields$ | async,
        analysisStates: micro.analysisStates$ | async,
        progress: micro.getProgress$ | async,
        filteredElements: micro.filteredItems$ | async,
        elements: micro.items$ | async,
        collapsedCategories: micro.collapsedCategories$ | async,
        taggingProtocol: micro.taggingProtocol$ | async,
        analysisContext: micro.analysisContext$ | async,
        nextCase: micro.nextCase$ | async,
        previousCase: micro.previousCase$ | async,
        nextSample: micro.nextSample$ | async,
        currentSample: micro.currentSample$ | async,
        currentCase: micro.currentCaseDisplay$ | async,
        currentWorkspace: micro.currentWorkspace$ | async,
        previousSample: micro.previousSample$ | async,
        hspan: micro.hspan$ | async,
        selectedAsset: micro.selectedAsset$ | async,
        hasUnsavedData: micro.unsynced$ | async,
        multiSelection: micro.multiSelection$ | async,
        favAssets: micro.currentAnalystFavouriteAssets$ | async,
        assetFilters: micro.assetFilters$ | async,
        selectedLabels: micro.selectedLabels$ | async,
        selectedRoiLabels: micro.selectedRoiLabels$ | async,
        totalCount: micro.totalActiveDetections$ | async,
        totalObjectsCount: micro.totalDetections$ | async,
        statsMode: micro.statsMode$ | async,
        selectedIndex: micro.selectedIndex$ | async,
        bookmarkedAsset: micro.bookmarkedAsset$ | async,
        dataEditable: micro.dataEditable$ | async,
        loadingProtocol: micro.loadingProtocol$ | async,
        loadingAnalysis: micro.findingsLoading$ | async,
        navigationLoading: micro.navigationLoading$ | async,
        navigationError: micro.navigationError$ | async,
        acquisitionEdition: micro.acquisitionEdition$ | async,
        numAssets: micro.totalAssets$ | async,
        syncing: micro.syncing$ | async,
        busy: micro.busy$ | async,
        mosaicEnabled: micro.mosaicEnabled$ | async,
        stitchingScans: micro.stitchingScans$ | async,
        viewerMode: micro.viewerMode$ | async,
        visibleLabels: micro.visibleLabels$ | async,
        pinnedLabels: micro.pinnedLabels$ | async,
        excludedFromCounter: micro.excludedFromCounter$ | async,
        tabContext: micro.tabActiveContext$ | async,
        activeDrawingMode: micro.drawingMode$ | async,
        selectedROIs: micro.selectedRois$ | async,
        availableViewerModes: micro.availableViewerModes$ | async,
        selectedToolIndex: micro.selectedToolIndex$ | async,
        rulerIcon: micro.rulerIcon$ | async,
        rulerSize: micro.rulerSize$ | async,
        rulerActive: micro.rulerActive$ | async,
        selectedPreset: micro.selectedPreset$ | async,
        imageFilters: micro.imageFilters$ | async,
        showPinned: micro.showPinned$ | async,
        availableDrawingModes: micro.availableDrawingModes$ | async,
        cursorClass: micro.cursor$ | async,
        tileSource: micro.tileSource$ | async,
        focusPoint: micro.focusPoint$ | async,
        overlays: micro.roiOverlays$ | async,
        highlightedRoiIds: micro.highlightedRoiIds$ | async,
        activeFindings: micro.activeFindings$ | async,
        labelCount: micro.activeAssetRoiCountsByLabel$ | async,
        totalActiveAssetRoiCount: micro.totalActiveAssetRoiCount$ | async,
        assetCategoryCounts: micro.assetCategoryCounts$ | async,
        sampleLabelCount: micro.activeLabelCounts$ | async,
        labelsInAllSelectedRois: micro.labelsInAllSelectedRois$ | async,
        labelsInSomeSelectedRois: micro.labelsInSomeSelectedRois$ | async,
        protocolPanelCollapsed: micro.protocolPanelCollapsed$ | async,
        aiAnalysisInProgress: micro.aiAnalysisInProgress$ | async,
        aiAnalysisStatus: micro.aiAnalysisStatus$ | async,
        aiAnalysisViewOnly: micro.aiAnalysisViewOnly$ | async,
        aiPollingExhausted: aiPollingExhausted$ | async
      } as state">
      <!-- WEB: shared analysis layout — top bar (top), protocol (left), viewer (remaining space).
           Same shell as the mosaic dashboard so both screens share one layout skeleton. -->
      <ts-analysis-layout *ngIf="state.layout === 'web'" class="full-height full-width">
        <!-- TOP BAR: breadcrumb · viewer tools · sample actions -->
        <ts-analysis-top-bar layoutTop variant="inline">
          <ts-navigation-breadcrumb barStart menuClass="micro-breadcrumb-menu"
            [levels]="micro.breadcrumbLevels$ | async"
            (optionSelected)="onBreadcrumbSelect($event, state.currentCase?.id, state.meOption, state.breadcrumbAnalysts)"
            (levelOpened)="onBreadcrumbLevelOpened($event, state.currentSample?.id, state.currentCase?.id)"
            (optionExpanded)="onBreadcrumbOptionExpanded($event)">
          </ts-navigation-breadcrumb>

          <!-- CENTER: viewer/drawing tools as plain toolbar buttons (mosaic style). The drawing
               (pencil) mode has no button of its own — its point/region tools are surfaced
               directly, and picking one enters drawing mode with that tool. The active tool is
               highlighted via the `applied` class. -->
          <div barCenter class="flex-row"
            *ngIf="authService.hasRoles(['admin', 'analyst', 'analystmanager']) && state.canEdit && state.availableViewerModes">
            <!-- Navigate / move -->
            <button *ngIf="state.availableViewerModes.includes(ViewerMode.idle)" mat-menu-item class="toolbar-action"
              [ngClass]="{ applied: state.viewerMode === ViewerMode.idle }"
              [matTooltip]="'tooltip.activate_idle_mode' | translate" [matTooltipShowDelay]="1000" data-cy="tool-idle"
              (click)="onToogleActiveMode(ViewerMode.idle)">
              <i class="ri-drag-move-2-line"></i>
            </button>

            <!-- ROI drawing tools: point / region (each enters drawing mode with that tool) -->
            <ng-container *ngIf="state.availableViewerModes.includes(ViewerMode.drawing)">
              <button mat-menu-item class="toolbar-action" data-cy="button-drawing-center"
                [ngClass]="{ applied: state.viewerMode === ViewerMode.drawing && state.activeDrawingMode === DrawingMode.center }"
                [matTooltip]="'core.point' | translate" [matTooltipShowDelay]="1000"
                (click)="onToogleActiveMode(ViewerMode.drawing); micro.setDrawingMode(DrawingMode.center)">
                <i class="ri-focus-3-line"></i>
              </button>
              <button mat-menu-item class="toolbar-action" data-cy="button-drawing-boundingBox"
                [ngClass]="{ applied: state.viewerMode === ViewerMode.drawing && state.activeDrawingMode === DrawingMode.boundingBox }"
                [matTooltip]="'core.region' | translate" [matTooltipShowDelay]="1000"
                (click)="onToogleActiveMode(ViewerMode.drawing); micro.setDrawingMode(DrawingMode.boundingBox)">
                <i class="ri-shape-line"></i>
              </button>
            </ng-container>

            <!-- Free-draw tools: brush / lasso (segmentation) -->
            <ng-container *ngIf="state.availableViewerModes.includes(ViewerMode.free)">
              <button mat-menu-item class="toolbar-action" data-cy="button-free-sketch"
                [ngClass]="{ applied: state.viewerMode === ViewerMode.free && state.activeDrawingMode === DrawingMode.sketch }"
                (click)="onToogleActiveMode(ViewerMode.free); micro.setDrawingMode(DrawingMode.sketch)">
                <i class="ri-brush-line"></i>
              </button>
              <button mat-menu-item class="toolbar-action" data-cy="button-free-lasso"
                [ngClass]="{ applied: state.viewerMode === ViewerMode.free && state.activeDrawingMode === DrawingMode.lasso }"
                (click)="onToogleActiveMode(ViewerMode.free); micro.setDrawingMode(DrawingMode.lasso)">
                <i class="ri-brush-fill"></i>
              </button>
            </ng-container>

            <!-- Select ROIs -->
            <button *ngIf="state.availableViewerModes.includes(ViewerMode.selecting)" mat-menu-item
              class="toolbar-action" [ngClass]="{ applied: state.viewerMode === ViewerMode.selecting }"
              [matTooltip]="'tooltip.activate_selecting_mode' | translate" [matTooltipShowDelay]="1000"
              data-cy="tool-selecting" (click)="onToogleActiveMode(ViewerMode.selecting)">
              <i class="ri-cursor-line"></i>
            </button>

            <!-- Delete selected ROIs — always red (destructive) with a count badge. -->
            <button *ngIf="state.viewerMode === ViewerMode.selecting && state.selectedROIs?.size" mat-menu-item
              class="toolbar-action delete-action" (click)="micro.removeAllLabels(state.selectedROIs)"
              [matTooltip]="'tooltip.remove_roi' | translate" [matTooltipShowDelay]="1000"
              data-cy="button-remove-selected-rois">
              <i class="ri-delete-bin-6-line"></i>
              <span class="selected-count-badge">{{ state.selectedROIs.size }}</span>
            </button>
          </div>

          <!-- RIGHT: microscope actions, inlined as plain toolbar buttons to match the mosaic. -->
          <div barEnd class="flex-row">
            <button *ngIfRole="['admin', 'analyst', 'analystmanager']" mat-menu-item class="toolbar-action"
              [matTooltip]="'tooltip.panel_mosaics_view' | translate" [matTooltipShowDelay]="1000"
              [disabled]="!state.mosaicEnabled || state.busy" (click)="onToggleAnalysisView('mosaic')"
              data-cy="mosaics-view-button">
              <i class="ri-grid-line"></i>
            </button>

            <!-- Center / fit view -->
            <button mat-menu-item class="toolbar-action" (click)="onCenterImage()"
              [matTooltip]="'tooltip.center_view' | translate" [matTooltipShowDelay]="1000"
              data-cy="button-center-view">
              <i class="ri-aspect-ratio-line"></i>
            </button>

            <!-- Zoom -->
            <button mat-menu-item class="toolbar-action" (click)="onZoomIn()"
              [matTooltip]="'tooltip.zoom_in' | translate" [matTooltipShowDelay]="1000" data-cy="button-zoom-in">
              <i class="ri-zoom-in-line"></i>
            </button>
            <button mat-menu-item class="toolbar-action" (click)="onZoomOut()"
              [matTooltip]="'tooltip.zoom_out' | translate" [matTooltipShowDelay]="1000" data-cy="button-zoom-out">
              <i class="ri-zoom-out-line"></i>
            </button>

            <button *ngIfRole="['admin', 'analyst', 'analystmanager']" mat-menu-item class="toolbar-action"
              [disabled]="!state.hspan" [matTooltip]="'tooltip.ruler' | translate" [matTooltipShowDelay]="1000"
              [matMenuTriggerFor]="rulerMenu" #rulerTrigger="matMenuTrigger" [ngClass]="{ applied: state.rulerActive }"
              data-cy="ruler-button">
              <i class="ri-ruler-line"></i>
            </button>

            <button *ngIfRole="['admin', 'analyst', 'analystmanager']" mat-menu-item class="toolbar-action"
              [matTooltip]="'tooltip.panel_image_filters' | translate" [matTooltipShowDelay]="1000"
              [matMenuTriggerFor]="filtersMenu" #filtersTrigger="matMenuTrigger"
              [ngClass]="{ applied: getImageFiltersApplied(state.imageFilters) }" data-cy="image-filters-button">
              <i class="ri-equalizer-line"></i>
            </button>

            <!-- Reference-strip asset filters (analyzed / favourites), moved out of the strip. -->
            <button mat-menu-item class="toolbar-action" [matMenuTriggerFor]="assetFiltersMenu"
              [matTooltip]="'tooltip.filters' | translate" [matTooltipShowDelay]="1000" data-cy="asset-filters-button"
              [ngClass]="{
                applied:
                  state.assetFilters.analyzed || state.assetFilters.favAcquisition || state.assetFilters.favAnalysis
              }">
              <i class="ri-filter-3-line"></i>
            </button>

            <ng-container *ngIf="state.isReviewingOtherAnalyst && !state.aiAnalysisViewOnly">
              <button *ngIfRole="['admin', 'analyst', 'analystmanager']" mat-menu-item class="toolbar-action"
                [disabled]="state.syncing" [ngClass]="{ applied: state.editingEnabled }" [matTooltip]="
                  (state.editingEnabled ? 'analysis.disable_editing_tooltip' : 'analysis.enable_editing_tooltip')
                    | translate
                " [matTooltipShowDelay]="1000" (click)="micro.toggleEditing(!state.editingEnabled)"
                data-cy="toolbar-toggle-editing">
                <i class="ri-edit-line" *ngIf="!state.editingEnabled"></i>
                <i class="ri-edit-fill" *ngIf="state.editingEnabled"></i>
              </button>
            </ng-container>

            <ng-container *ngIf="state.isReviewingOtherAnalyst && !state.aiAnalysisViewOnly && !state.hasUnsavedData">
              <button *ngIfRole="['admin', 'analyst', 'analystmanager']" mat-menu-item class="toolbar-action"
                [disabled]="state.syncing" [matTooltip]="'info.accept_complete_analysis_tooltip' | translate"
                [matTooltipShowDelay]="1000" (click)="onAcceptCompleteAnalysis()" data-cy="toolbar-accept-analysis">
                <i class="ri-checkbox-circle-line"></i>
              </button>
            </ng-container>

            <button *ngIfRole="['admin', 'analyst', 'analystmanager']" mat-menu-item class="toolbar-action"
              [matTooltip]="'analysis.revert_all_changes' | translate" [matTooltipShowDelay]="1000"
              [disabled]="!state.hasUnsavedData || !state.editingActive" (click)="onDiscardChanges()"
              data-cy="toolbar-revert-all-changes">
              <i class="ri-arrow-go-back-line"></i>
            </button>

            <button [disabled]="state.syncing || !state.hasUnsavedData" mat-menu-item class="toolbar-action"
              matTooltip="{{ 'button.save' | translate }}" [matTooltipShowDelay]="1000"
              [class.bg-warning]="state.hasUnsavedData && !state.syncing" [class.text-white]="state.hasUnsavedData"
              (click)="onSave()" *ngIfRole="['admin', 'analyst', 'analystmanager']" data-cy="toolbar-save-analysis">
              <mat-spinner *ngIf="state.syncing; else microSaveIcon" class="relative-spinner" value="indeterminate"
                [diameter]="20"></mat-spinner>
              <ng-template #microSaveIcon>
                <i class="ri-save-line"></i>
              </ng-template>
            </button>

            <!-- More tools (favourite / bookmark / downloads), grouped into one dropdown. -->
            <button mat-menu-item class="toolbar-action" [matMenuTriggerFor]="fabMenu" data-cy="FAB-tools">
              <i class="ri-more-2-line"></i>
            </button>

            <button mat-menu-item class="toolbar-action" matTooltip="{{ 'button.close' | translate }}"
              [matTooltipShowDelay]="1000" [disabled]="state.busy" (click)="onExit()" data-cy="toolbar-exit">
              <i class="ri-close-fill"></i>
            </button>
          </div>
        </ts-analysis-top-bar>

        <!-- Ruler / image-filter dropdowns for the bar's ruler & filter buttons. mat-menu is
             template-only (portals on open), so declaring these inside the shell adds no layout box. -->
        <mat-menu #rulerMenu="matMenu" class="widget analysis-tool-menu">
          <ts-ruler-selector [rulerIcon]="state.rulerIcon" [rulerSize]="state.rulerSize"
            (changeRulerIcon)="micro.changeRulerIcon($event)" (changeRulerSize)="micro.changeRulerSize($event)"
            (resetRuler)="onRulerReset()"></ts-ruler-selector>
        </mat-menu>
        <mat-menu #filtersMenu="matMenu" class="widget analysis-tool-menu">
          <ts-image-filters [filters]="state.imageFilters" [selectedPreset]="state.selectedPreset"
            (updateFilter)="micro.updateImageFilter($event)" (presetFilters)="micro.updateImageFilters($event)"
            (resetFilters)="onFiltersReset()"></ts-image-filters>
        </mat-menu>
        <!-- Reference-strip asset filters. Plain buttons (not mat-menu-item) + stopPropagation so
             toggling one filter keeps the menu open for multi-select. -->
        <mat-menu #assetFiltersMenu="matMenu" class="widget analysis-tool-menu">
          <div class="asset-filter-menu" (click)="$event.stopPropagation()">
            <button *ngIfRole="['admin', 'analyst', 'analystmanager']" type="button" class="asset-filter-option"
              [class.active]="state.assetFilters.analyzed" (click)="micro.updateAssetFilters('analyzed')"
              data-cy="filter-analyzed">
              <i class="ri-shape-line"></i>
              <span>{{ 'tooltip.analyzed_images_filter' | translate }}</span>
              <i *ngIf="state.assetFilters.analyzed" class="ri-check-line check-icon"></i>
            </button>
            <button type="button" class="asset-filter-option" [class.active]="state.assetFilters.favAcquisition"
              (click)="micro.updateAssetFilters('favAcquisition')" data-cy="filter-fav">
              <i
                [ngClass]="{ 'ri-star-line': !state.assetFilters.favAcquisition, 'ri-star-fill': state.assetFilters.favAcquisition }"></i>
              <span>{{ 'tooltip.acquisition_favourite_filter' | translate }}</span>
              <i *ngIf="state.assetFilters.favAcquisition" class="ri-check-line check-icon"></i>
            </button>
            <button *ngIfRole="['admin', 'analyst', 'analystmanager']" type="button" class="asset-filter-option"
              [class.active]="state.assetFilters.favAnalysis" (click)="micro.updateAssetFilters('favAnalysis')"
              data-cy="filter-fav-analysis">
              <i
                [ngClass]="{ 'ri-heart-3-line': !state.assetFilters.favAnalysis, 'ri-heart-3-fill': state.assetFilters.favAnalysis }"></i>
              <span>{{ 'tooltip.analysis_favourite_filter' | translate }}</span>
              <i *ngIf="state.assetFilters.favAnalysis" class="ri-check-line check-icon"></i>
            </button>
          </div>
        </mat-menu>
        <!-- More tools dropdown: minimap toggle + bulk download. The per-asset actions (favourite /
             bookmark / download original) moved to the reference-strip thumbnail context menu below. -->
        <mat-menu #fabMenu="matMenu" class="widget analysis-tool-menu">
          <button mat-menu-item (click)="toggleNavigator()" data-cy="button-toggle-navigator">
            <i [class.ri-map-2-fill]="navigatorVisible" [class.ri-map-2-line]="!navigatorVisible"></i>
            <span>{{ (navigatorVisible ? 'label.hide_minimap' : 'label.show_minimap') | translate }}</span>
          </button>

          <button *ngIf="state.numAssets > 1" mat-menu-item [disabled]="!featureService.canUse('downloadAvailable')"
            (click)="onDownloadAssets(true, state.elements, state.currentSample?.name)"
            data-cy="button-download-assets-zip">
            <i class="ri-folder-download-line"></i>
            <span>{{ 'button.download_zip_images' | translate }}</span>
          </button>
        </mat-menu>

        <!-- Per-thumbnail actions: opened by right-clicking a reference-strip thumbnail. Anchored to
             an invisible trigger placed at the cursor (see `onThumbnailContextMenu`). Icons are
             coloured here (unlike the neutral toolbar) to read as quick, distinct actions. -->
        <div #thumbMenuTrigger="matMenuTrigger" [matMenuTriggerFor]="thumbnailActionsMenu" [style.position]="'fixed'"
          [style.left.px]="thumbMenuX" [style.top.px]="thumbMenuY" style="visibility: hidden"></div>
        <mat-menu #thumbnailActionsMenu="matMenu" class="widget analysis-tool-menu thumbnail-actions-menu">
          <button *ngIfRole="['admin', 'analyst', 'analystmanager']" mat-menu-item [disabled]="!state.canEdit"
            (click)="micro.setAsFavourite(thumbMenuItem?.id)" data-cy="thumbnail-favourite">
            <i class="thumbnail-action-icon thumbnail-action-icon--fav"
              [class.ri-heart-3-fill]="state.favAssets.includes(thumbMenuItem?.id)"
              [class.ri-heart-3-line]="!state.favAssets.includes(thumbMenuItem?.id)"></i>
            <span>{{
              (state.favAssets.includes(thumbMenuItem?.id) ? 'label.marked_fav_analysis' : 'label.mark_fav_analysis')
              | translate }}</span>
          </button>
          <button *ngIfRole="['admin', 'analyst', 'analystmanager']" mat-menu-item [disabled]="!state.canEdit"
            (click)="micro.toggleBookmark(thumbMenuItem?.id)" data-cy="thumbnail-bookmark">
            <i class="thumbnail-action-icon thumbnail-action-icon--bookmark"
              [class.ri-bookmark-fill]="state.bookmarkedAsset === thumbMenuItem?.id"
              [class.ri-bookmark-line]="state.bookmarkedAsset !== thumbMenuItem?.id"></i>
            <span>{{
              (state.bookmarkedAsset === thumbMenuItem?.id ? 'tooltip.remove_bookmark' : 'tooltip.set_bookmark')
              | translate }}</span>
          </button>
          <button mat-menu-item [disabled]="!featureService.canUse('downloadAvailable')"
            (click)="downloadOriginalImage(thumbMenuItem)" data-cy="thumbnail-download">
            <i class="thumbnail-action-icon thumbnail-action-icon--download ri-download-2-line"></i>
            <span>{{ 'button.download_original_image' | translate }}</span>
          </button>
        </mat-menu>

        <!-- PROTOCOL PANEL (left column of the shell body) -->
        <div class="micro-analysis-panel-container" [class.protocol-collapsed]="state.protocolPanelCollapsed"
          *ngIf="authService.hasRoles(['admin', 'analyst', 'analystmanager']) || state.acquisitionFields?.length">
          <!-- Collapse/expand affordance: a small arrow centred on the panel's inner
               edge (same idea as the reference-strip toggle), always reachable. -->
          <button *ngIfRole="['admin', 'analyst', 'analystmanager']" class="protocol-collapse-toggle"
            [matTooltip]="(state.protocolPanelCollapsed ? 'tooltip.show_panel' : 'tooltip.hide_panel') | translate"
            [matTooltipShowDelay]="0" (click)="micro.toggleProtocolPanel()" data-cy="toggle-protocol-panel-button">
            <i [class]="state.protocolPanelCollapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'"></i>
          </button>
          <ts-sample-analysis-panel #analysisPanel [collapsed]="state.protocolPanelCollapsed"
            [dataEditable]="state.dataEditable" [taggingProtocol]="state.taggingProtocol"
            [segmentation]="state.segmentation" [multiSelection]="state.multiSelection"
            [selectedAsset]="state.selectedAsset" [canEdit]="state.editingActive" [sampleId]="state.currentSample?.id"
            [currentAnalyst]="state.selectedAnalyst" [loadingProtocol]="state.loadingProtocol"
            [loadingAnalysis]="state.loadingAnalysis" [analysisContext]="state.analysisContext"
            [pipelines]="state.pipelines" [categories]="state.categories" [options]="state.labels" [steps]="state.steps"
            [acquisitionFields]="state.acquisitionFields" [showPinned]="state.showPinned"
            [stitchingScans]="state.stitchingScans" [acquisitionEdition]="state.acquisitionEdition"
            [selectedLabels]="state.selectedLabels" [visibleLabels]="state.visibleLabels"
            [pinnedLabels]="state.pinnedLabels" [collapsedCategories]="state.collapsedCategories"
            [excludedFromCounter]="state.excludedFromCounter" [activeFindings]="state.activeFindings"
            [labelCount]="state.labelCount" [sampleLabelCount]="state.sampleLabelCount" [statsMode]="state.statsMode"
            [totalCount]="state.totalCount" [totalActiveAssetRoiCount]="state.totalActiveAssetRoiCount"
            [assetCategoryCounts]="state.assetCategoryCounts" [totalObjectsCount]="state.totalObjectsCount"
            [hasSelectedRois]="(state.selectedROIs?.size ?? 0) > 0"
            [labelsInAllSelectedRois]="state.labelsInAllSelectedRois || emptySet"
            [labelsInSomeSelectedRois]="state.labelsInSomeSelectedRois || emptySet"
            [forceExpanded]="(state.selectedROIs?.size ?? 0) > 0" (toogleMode)="micro.setProtocolContext($event)"
            (setStatsMode)="micro.setStatsMode($event)" (toggleMulti)="micro.toggleMultiSelection()"
            (tooglePinned)="micro.togglePinnedOptions()" (saveAssetData)="saveAssetData()"
            (discardDataChanges)="micro.discardDataChanges()"
            (acquisitionEditionMode)="micro.toogleAcquisitionEdition($event)"
            (acquisitionFieldChange)="micro.updateAssetDataField($event)"
            (toogleTaskPanel)="micro.collapseCategory($event)" (selectLabel)="micro.onSelectLabel($event)"
            (newLabel)="onNewLabel()" (pinLabel)="micro.onPinnedLabel($event)"
            (counterLabel)="micro.onCounterLabel($event)" (visibleLabel)="micro.onVisibleLabel($event)"
            (createFinding)="micro.createFinding($event)" (updateFinding)="micro.updateFinding($event)"
            (hoverLabel)="micro.setHoveredLabel($event)">
            <!-- Label panel projected between the controls bar and the
               expansion panels. Two mutually-exclusive modes: -->
            <!-- (a) Drawing: the tags that will be applied to new ROIs. -->
            <ts-label-selection-panel labelSelection
              *ngIf="state.multiSelection && (state.selectedROIs?.size ?? 0) === 0" [labels]="state.selectedLabels"
              (clearLabel)="micro.selectLabel($event)"
              (clearAll)="micro.clearSelectedLabels()"></ts-label-selection-panel>
            <!-- (b) ROI selected: the labels on the selection, for quick removal. -->
            <ts-label-selection-panel labelSelection *ngIf="(state.selectedROIs?.size ?? 0) > 0"
              [labels]="state.selectedRoiLabels" (clearLabel)="micro.removeSelectedRoiLabel($event)"
              (clearAll)="micro.clearSelectedRoiLabels()"></ts-label-selection-panel>
          </ts-sample-analysis-panel>
        </div>

        <!-- VIEWER AREA (main column) — the DZI viewer fills it; the reference strip + minimap are
             anchored within it (position: relative), so they stay inside the viewer, not the screen. -->
        <div class="micro-viewer-area" [class.refstrip-collapsed]="!refStripVisible">
          <ng-container *ngTemplateOutlet="viewerContentTpl"></ng-container>

          <!-- BOTTOM REFSTRIP -->
          <div class="refstrip-container">
            <ts-sample-reference-strip [items]="state.filteredElements" [viewedItems]="micro.viewedAssets$ | async"
              [reviewedAssets]="micro.reviewedAssets$ | async" [analyzedAssets]="micro.analyzedAssets$ | async"
              [favAssets]="state.favAssets" [selectedIndex]="state.selectedIndex"
              [bookmarkedAsset]="state.bookmarkedAsset" (changeIndex)="micro.navigateToIndex($event)"
              (visibilityChanged)="refStripVisible = $event" (thumbnailContextMenu)="onThumbnailContextMenu($event)">
              <!-- SAMPLE PROGRESS — thin bar riding the strip's top edge; hides with the strip. -->
              <div id="progress-container">
                <div class="progressbar-fill" [style.width]="state.progress + '%'"></div>
                <span class="progress-percentage">{{ state.progress }}%</span>
              </div>
            </ts-sample-reference-strip>
          </div>
        </div>
      </ts-analysis-layout>

      <!-- MOBILE: full-bleed viewer with a single top actions row (no shared shell / side body). -->
      <div *ngIf="state.layout === 'mobile'" class="position-relative full-width full-height">
        <ng-container *ngTemplateOutlet="viewerContentTpl"></ng-container>

        <!-- ANALYSIS ACTIONS AND PANELS CONTAINER -->
        <ng-container [ngTemplateOutlet]="mobileAnalysisPanelTemplate"></ng-container>
        <div class="analysis-panel-and-actions-container mobile">
          <div class="sample-actions-and-progress-container">
            <div id="mobile-tab-headers" class="flex-row flex-align-center">
              <div class="flex-row">
                <button class="toolbar-action btn float-left" matTooltip="{{ 'button.close' | translate }}"
                  [disabled]="state.busy" (click)="onExit()" [routerLink]="[]" queryParamsHandling="merge"
                  data-cy="toolbar-exit">
                  <i class="ri-close-fill"></i>
                </button>
                <button class="toolbar-action btn caution float-left mx-3" matTooltip="{{ 'button.save' | translate }}"
                  [routerLink]="['./']" queryParamsHandling="merge" [class.bg-warning]="state.hasUnsavedData"
                  [class.text-white]="state.hasUnsavedData" [disabled]="!state.hasUnsavedData" (click)="onSave()"
                  *ngIfRole="['admin', 'analyst', 'analystmanager']" data-cy="toolbar-save-analysis">
                  <i class="ri-save-line"></i>
                </button>
              </div>
              <div class="ml-auto">
                <button class="toolbar-action btn float-right mx-3" #btn [matMenuTriggerFor]="menuHeaders"
                  data-cy="button-switch-analysis-context">
                  <i [ngClass]="{
                      'ri-image-line': state.analysisContext === 'asset',
                      'ri-grid-line': state.analysisContext === 'sample'
                    }"></i>
                </button>
                <mat-menu #menuHeaders="matMenu">
                  <a mat-menu-item class="toolbar-action auto flex-row align-items-center" (click)="toggleAsset()"
                    role="button" data-cy="button-switchto-asset-analysis">
                    <i class="ri-image-line" [class.text-primary]="state.analysisContext === 'asset'"></i>
                    <span>{{ 'core.analysis_image' | translate | uppercase }}</span>
                  </a>
                  <a mat-menu-item class="toolbar-action auto flex-row align-items-center" (click)="toggleAsset()"
                    role="button" data-cy="button-switchto-sample-analysis">
                    <i class="ri-grid-line" [class.text-primary]="state.analysisContext === 'sample'"></i>
                    <span>{{ 'core.analysis_sample' | translate | uppercase }}</span>
                  </a>
                  <span style="flex-grow: 1"></span>
                </mat-menu>
              </div>
            </div>
            <!-- SAMPLE ANALYSIS TOOLS (shared template, see below) -->
            <ng-container *ngTemplateOutlet="sampleToolsTpl"></ng-container>
          </div>
        </div>
      </div>

      <!-- Shared viewer content: DZI viewer + loading overlay. Outletted once into whichever layout
           branch (web shell viewer-area or mobile full-bleed) is active, so the heavy viewer with
           its bindings/directives lives in a single place. `state` resolves from the enclosing
           *ngIf…as state. -->
      <ng-template #viewerContentTpl>
        <!-- DZI VIEWER -->
        <ts-dzi-viewer tsDziOverlaysViewer tsDziOverlaysEditor tsDziOverlaysDrawer tsDziKeyBindings tsDziMouseBindings
          tsDziImageFilters tsDziScalebar tsDziRuler [tileSource]="state.tileSource" [zoomLevel]="0.5"
          [focusPoint]="state.focusPoint" [showNavigator]="true" [cursorClass]="state.cursorClass"
          [overlays]="state.overlays" [highlightedIds]="state.highlightedRoiIds" [viewerMode]="state.viewerMode"
          [drawingMode]="state.activeDrawingMode" [defaultWidth]="(micro.currentBoxDimensions$ | async)?.defaultWidth"
          [defaultHeight]="(micro.currentBoxDimensions$ | async)?.defaultHeight" [imageFilters]="state.imageFilters"
          [hspan]="state.hspan" [rulerIcon]="state.rulerIcon" [rulerSize]="state.rulerSize"
          (overlaySelected)="micro.selectRois($event)" (overlayUpdated)="onOverlayUpdated($event)"
          (overlayAdded)="onOverlayAdded($event, state.selectedLabels)" (ctrlS)="onSave()"
          (ctrlMeta)="onCtrlMeta($event, !state.editingActive, state.viewerMode)"
          (space)="onSpace($event, state.viewerMode)" (esc)="onEscape($event)" (delete)="onDelete(state.selectedROIs)"
          (leftArrow)="micro.navigatePrevious()" (rightArrow)="micro.navigateNext()" (mouseMove)="onMouseMove($event)"
          (mouseDown)="onMouseDown($event, state.viewerMode)" (mouseUp)="onMouseUp()"
          (rightClick)="onContextMenu($event)" (opened)="micro.onImageOpened()">
        </ts-dzi-viewer>

        <!-- LOADING / ERROR OVERLAY (shown while navigating or if the image failed) -->
        <div class="viewer-loading-overlay" *ngIf="state.navigationLoading || state.navigationError">
          <ng-container *ngIf="state.navigationError; else loadingState">
            <i class="ri-image-off-line viewer-loading-icon"></i>
            <span class="viewer-loading-text">{{ 'info.image_unavailable' | translate }}</span>
          </ng-container>
          <ng-template #loadingState>
            <mat-spinner [diameter]="48" color="accent"></mat-spinner>
            <span class="viewer-loading-text">{{ 'info.loading_data' | translate }}</span>
          </ng-template>
        </div>
      </ng-template>

      <!-- Sample analysis tools: used by the mobile actions row, so its ~30 bindings live in one
           place. `state` resolves from the enclosing *ngIf…as state. -->
      <ng-template #sampleToolsTpl>
        <ts-sample-analysis-tools [numAssets]="state.numAssets" [mosaicMode]="false" [analystInBreadcrumb]="true"
          [hspan]="state.hspan" [layout]="state.layout" [selectedAsset]="state.selectedAsset"
          [selectedIndex]="state.selectedIndex" [availableAIanalysis]="featureService.canUse('ai')"
          [downloadAvailable]="featureService.canUse('downloadAvailable')" [hasUnsavedData]="state.hasUnsavedData"
          [syncing]="state.syncing" [busy]="state.busy" [mosaicButtonDisabled]="!state.mosaicEnabled"
          [sectorProcessing]="state.aiAnalysisViewOnly" [aiAnalysisStatus]="state.aiAnalysisStatus"
          [aiPollingExhausted]="state.aiPollingExhausted" [aiAnalysisViewOnly]="state.aiAnalysisViewOnly"
          [aiAlgorithms]="state.aiAlgorithms" [analystStates]="state.analysisStates" [userAnalysts]="state.userAnalysts"
          [analystSelected]="state.selectedAnalyst" [isAnalystCurrentUser]="state.canEdit"
          [canEdit]="state.editingActive" [imageFilters]="state.imageFilters"
          [imageFiltersApplied]="getImageFiltersApplied(state.imageFilters)" [selectedPreset]="state.selectedPreset"
          [rulerActive]="state.rulerActive" [rulerIcon]="state.rulerIcon" [rulerSize]="state.rulerSize"
          [panelContext]="state.tabContext" [editingEnabled]="state.editingEnabled"
          [isReviewingOtherAnalyst]="state.isReviewingOtherAnalyst" (exit)="onExit()" (save)="onSave()"
          (acceptCompleteAnalysis)="onAcceptCompleteAnalysis()"
          (downloadAll)="onDownloadAssets(true, state.elements, state.currentSample?.name)"
          (toggleMode)="onToggleAnalysisView($event)" (updateFilter)="micro.updateImageFilter($event)"
          (updateFilters)="micro.updateImageFilters($event)" (resetFilters)="micro.resetImageFilters()"
          (resetRuler)="micro.resetRuler()" (analystChange)="onAnalystChange($event)"
          (toogleReference)="micro.setReference()" (tooglePanel)="micro.setTabContext($event)"
          (changeRulerIcon)="micro.changeRulerIcon($event)" (changeRulerSize)="micro.changeRulerSize($event)"
          (downloadAsset)="onDownloadAsset()" (toggleEditing)="micro.toggleEditing($event)"
          (revertAllChanges)="onDiscardChanges()">
        </ts-sample-analysis-tools>
      </ng-template>
    </ng-container>
  </ng-template>

  <!-- Responsive Analysis Panel -->
  <ng-template #mobileAnalysisPanelTemplate>
    <ng-container *ngIf="micro.selectedRois$ | async as selectedROIs">
      <ts-mobile-analysis-panel [hidden]="(showMobileAnalysisPanel$ | async) === false"
        (onclose)="closeMobileAnalysisPanel()"
        (delete)="micro.removeAllLabels(selectedROIs)"></ts-mobile-analysis-panel>
    </ng-container>
  </ng-template>

  <!-- Context Menu -->
  <ts-label-context-menu *ngIf="(micro.contextMenuVisible$ | async) === true"
    [categories]="(micro.taggingCategories$ | async) || {}" [options]="(micro.scopeOptions$ | async) || {}"
    [checkedOptions]="(micro.selectedItemLabels$ | async) || []"
    [doubleCheckedOptions]="(micro.doubleCheckedOptions$ | async) || []"
    (optionSelected)="micro.onContextMenuOptionSelected($event)"
    (optionDeselected)="micro.onContextMenuOptionDeselected($event)" #contextMenu [style.position]="'fixed'"
    [style.z-index]="'1000'" [style.top.px]="(micro.contextMenuPosition$ | async)?.y || 0"
    [style.left.px]="(micro.contextMenuPosition$ | async)?.x || 0"></ts-label-context-menu>
</div>
